/**
 * Registro canônico de notificações.
 *
 * `notifications.type` é TEXT, com CHECK quando o histórico é compatível.
 * Este arquivo define os tipos e suas categorias de preferência
 * (`notification_preferences.category`).
 *
 *QUEM USA O QUÊ:
 * - `supabase/functions/_shared/notify.ts` → decide se o push é entregue.
 *   Espelho Deno deste arquivo (não há import compartilhado entre Deno e npm).
 * - `apps/ios/.../NotificationCategory.swift` → mostra a categoria na UI.
 * - `apps/web/src/lib/notification-meta.ts` → ícone, rota e label do item.
 *
 * AO ADICIONAR UM TIPO: acrescente aqui, no espelho Deno, no enum Swift, no
 * CHECK por uma nova migration (sem editar o histórico) e rode
 * `npm test -w @tickethall/core` — o teste de sync falha se os mapas divergirem.
 */

/** Categorias de preferência. Espelha `notification_preferences.category`. */
export const NOTIFICATION_CATEGORIES = [
  'purchase',
  'reminder',
  'promo',
  'transfer',
  'refund',
  'message',
  'security',
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/** Default de `push` quando o usuário nunca abriu a tela de preferências. */
export const NOTIFICATION_CATEGORY_DEFAULT_PUSH: Record<NotificationCategory, boolean> = {
  purchase: true,
  reminder: true,
  promo: false,
  transfer: true,
  refund: true,
  message: true,
  security: true,
};

/** `security` é sempre entregue — o usuário não pode desligar. */
export function isCategoryForcedOn(category: NotificationCategory): boolean {
  return category === 'security';
}

/** Categorias que a UI de preferências deve oferecer como toggle. */
export function userConfigurableCategories(): NotificationCategory[] {
  return NOTIFICATION_CATEGORIES.filter((c) => !isCategoryForcedOn(c));
}

/**
 * Mapa exato `type` → categoria. Uma linha por valor que o backend emite.
 *
 * Antes deste mapa a categorização era ad-hoc (`type.includes(...)`) e 18 dos
 * 28 tipos caíam no `return 'purchase'` final — inclusive `lgpd_deletion`, que
 * virava "compra" e por isso seguia a preferência errada.
 */
export const NOTIFICATION_TYPE_CATEGORIES = {
  // purchase — compra, venda e estado do ingresso
  purchase_confirmed: 'purchase',
  resale_purchase_confirmed: 'purchase',
  resale_purchased: 'purchase',
  resale_sold: 'purchase',
  resale_listed: 'purchase',
  resale_expired: 'purchase',
  resale_reversed: 'purchase',
  resale_disputed: 'purchase',
  resale_payment_failed: 'purchase',
  certificate_issued: 'purchase',
  queue_admitted: 'purchase',
  test_notification: 'purchase',

  // reminder — antes do evento começar
  reminder_24h: 'reminder',
  reminder_1h: 'reminder',

  // transfer — ingresso muda de mão
  transfer_sent: 'transfer',
  transfer_received: 'transfer',

  // refund — dinheiro ou ingresso volta para o usuário
  order_refunded: 'refund',
  chargeback: 'refund',
  ticket_cancelled: 'refund',
  event_cancelled: 'refund',
  wallet_withdrawal_requested: 'refund',
  wallet_withdrawal_failed: 'refund',

  // message — comunicação entre comprador e produtor
  producer_message_received: 'message',
  producer_reply: 'message',
  bulk_message: 'message',

  // security — conta e LGPD
  lgpd_deletion: 'security',
  role_upgrade_request: 'security',
  role_upgrade_approved: 'security',
  role_upgrade_rejected: 'security',

  // promo — sem emissor no backend hoje; fica no registro para as
  // preferências do usuário não se perderem quando o emissor chegar.
} as const satisfies Record<string, NotificationCategory>;

export type NotificationType = keyof typeof NOTIFICATION_TYPE_CATEGORIES;

/** Todos os valores aceitos em `notifications.type`. */
export const NOTIFICATION_TYPES = Object.keys(
  NOTIFICATION_TYPE_CATEGORIES,
) as NotificationType[];

/**
 * Prefixos abertos: `reminder_6h` funciona sem tocar no mapa.
 * Mantido porque `event-reminders` monta o type dinamicamente.
 */
const CATEGORY_PREFIXES: ReadonlyArray<readonly [string, NotificationCategory]> = [
  ['reminder', 'reminder'],
  ['transfer', 'transfer'],
  ['refund', 'refund'],
  ['resale', 'purchase'],
  ['wallet_withdrawal', 'refund'],
];

/**
 * Rede de segurança por palavra-chave, só para tipos que ainda não existem.
 *
 * `promo` não tem emissor hoje — se o primeiro emissor entrar sem tocar no mapa,
 * o fallback errado seria `purchase`, cujo push é ligado por default. O
 * oposto de respeitar a preferência. Mesmo formato do iOS.
 */
const CATEGORY_KEYWORDS: ReadonlyArray<readonly [readonly string[], NotificationCategory]> = [
  [['promo', 'offer', 'coupon'], 'promo'],
  [['security', 'login', 'password'], 'security'],
];

/**
 * `notifications.type` → categoria de preferência.
 *
 * Type desconhecido cai em `purchase` (o default histórico) em vez de
 * `undefined`, porque a coluna é TEXT e o CHECK pode laggingar de um deploy.
 */
export function notificationCategoryForType(
  type: string | null | undefined,
): NotificationCategory {
  const normalized = (type ?? '').trim().toLowerCase();
  if (!normalized) return 'purchase';

  const exact = (NOTIFICATION_TYPE_CATEGORIES as Record<string, NotificationCategory>)[normalized];
  if (exact) return exact;

  for (const [prefix, category] of CATEGORY_PREFIXES) {
    if (normalized.startsWith(prefix)) return category;
  }

  for (const [keywords, category] of CATEGORY_KEYWORDS) {
    if (keywords.some((word) => normalized.includes(word))) return category;
  }

  return 'purchase';
}

/** Catálogo ordenado, útil para UI, docs e testes. */
export function notificationTypeCatalog(): Array<{
  type: NotificationType;
  category: NotificationCategory;
}> {
  return NOTIFICATION_TYPES.map((type) => ({
    type,
    category: (NOTIFICATION_TYPE_CATEGORIES as Record<string, NotificationCategory>)[type],
  }));
}
